import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, Radio, Repeat, Hotel, Home, Tag, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve();
  s.onerror = () => reject(new Error("Failed to load Razorpay Checkout"));
  document.body.appendChild(s);
});

const inr = (n) => Number(n || 0).toLocaleString("en-IN");

export default function BillingStep({ data, reload, onActivated }) {
  const nav = useNavigate();
  const { property, units, invoice } = data;
  const isHotel = (invoice?.category || property.category || "homestay").toLowerCase() === "hotel";
  const [vpa, setVpa] = useState("atitihost@okhdfcbank");
  const [autodebit, setAutodebit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);

  const applyPromo = () => {
    const code = promo.trim().toUpperCase();
    if (!code) return;
    if (code === "NEW25") {
      const discount = Math.round((invoice?.subtotal || 0) * 0.25 * 100) / 100;
      setApplied({ code, label: "Launch offer · 25% off your PMS subscription", discount });
      toast.success(`Promo NEW25 applied — you save ₹${inr(discount)}`);
    } else {
      setApplied(null);
      toast.error("Invalid promo code");
    }
  };
  const clearPromo = () => { setApplied(null); setPromo(""); };
  const discountedSubtotal = Math.max(0, (invoice?.subtotal || 0) - (applied?.discount || 0));
  const finalGst = Math.round(discountedSubtotal * 0.18 * 100) / 100;
  const finalTotal = Math.round((discountedSubtotal + finalGst) * 100) / 100;

  const pay = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post(`/pms/properties/${property.id}/pay/order`,
        applied ? { promo_code: applied.code } : {});
      if (order.mode === "razorpay") {
        await loadRazorpay();
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "Atiti PMS",
          description: `PMS activation — ${property.name}`,
          prefill: order.prefill || {},
          theme: { color: "#4F46E5" },
          modal: { ondismiss: () => setBusy(false) },
          handler: async (resp) => {
            try {
              await api.post(`/pms/properties/${property.id}/pay/verify`, {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success(`${property.name} PMS is now ACTIVE — all ${units.length} units activated`);
              await reload();
              onActivated();
            } catch (e) { toast.error(errMsg(e)); }
            setBusy(false);
          },
        });
        rzp.on("payment.failed", (e) => { toast.error(e?.error?.description || "Payment failed"); setBusy(false); });
        rzp.open();
        return; // Checkout handler drives the rest
      }
      // mock / preview path — backend already updated invoice.final_total
      await api.post(`/pms/properties/${property.id}/pay`, { vpa, autodebit });
      toast.success(`${property.name} PMS is now ACTIVE — all ${units.length} units activated`);
      await reload();
      onActivated();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (property.status === "active") {
    return (
      <div className="max-w-2xl mx-auto text-center py-10 reveal">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto pulse-success"><CheckCircle2 size={30} /></div>
        <h2 className="font-display text-2xl font-extrabold text-slate-900 mt-5">{property.name} — PMS Active</h2>
        <p className="text-sm text-slate-500 mt-2">All {units.length} units are on an active subscription (trigger: upfront payment).<br />PMS Active means the tool is unlocked for this property — nothing is live on external channels yet.</p>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mt-6 text-left">
          <div className="grid grid-cols-3 gap-3">
            {units.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
                <span className="h-7 px-2 min-w-[28px] max-w-[120px] rounded-lg bg-slate-900 text-white font-display font-bold text-[11px] flex items-center justify-center truncate">{u.name}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">active</span>
              </div>
            ))}
          </div>
          {property.autodebit && <p className="text-xs text-slate-400 mt-4 flex items-center gap-1.5"><Repeat size={12} /> UPI auto-debit mandate enabled for monthly renewals.</p>}
        </div>
        <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
          <button data-testid="billing-launch-pms-button" onClick={() => nav("/pms/launch")}
            className="btn-brand rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2"><CheckCircle2 size={16} /> Launch PMS</button>
          <button data-testid="goto-channels-button" onClick={() => nav(`/pms/property/${property.id}/channels`)}
            className="rounded-full px-8 py-3.5 text-sm font-semibold inline-flex items-center gap-2 border border-slate-300 text-slate-600 hover:border-slate-500 bg-white"><Radio size={16} /> Connect channels first</button>
        </div>
      </div>
    );
  }

  if (!invoice) return <p className="text-sm text-slate-400">No invoice yet — finish listing mapping first.</p>;

  // ─── HOTEL-TIER: compact smart summary ────────────────────────────────
  if (isHotel) {
    const displayTotal = applied ? finalTotal : invoice.total;
    return (
      <div className="reveal max-w-3xl mx-auto space-y-5">
        <div className="bg-gradient-to-br from-indigo-600 via-indigo-500 to-indigo-700 rounded-3xl p-7 text-white shadow-xl">
          <div className="flex items-center gap-3">
            <span className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center"><Hotel size={20} /></span>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-indigo-100">Hotel-tier subscription</p>
              <h2 className="font-display font-bold text-xl mt-0.5">{property.name}</h2>
            </div>
            <span data-testid="tier-badge" className="ml-auto text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/15 text-white border border-white/25">₹300/unit</span>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3 text-center bg-white/10 backdrop-blur rounded-xl p-4 border border-white/20">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-indigo-100">Units</p>
              <p className="font-display font-bold text-2xl mt-0.5" data-testid="hotel-invoice-units">{invoice.unit_count}</p>
            </div>
            <div className="border-x border-white/15">
              <p className="text-[10px] uppercase tracking-wider text-indigo-100">Rate</p>
              <p className="font-display font-bold text-2xl mt-0.5">₹{invoice.price_per_unit}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-indigo-100">Subtotal</p>
              <p className="font-display font-bold text-2xl mt-0.5">₹{inr(invoice.subtotal)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="space-y-2.5 text-sm">
            <Line label={`${invoice.unit_count} units × ₹${invoice.price_per_unit}`} value={`₹${inr(invoice.subtotal)}`} />
            {applied && (
              <Line data-testid="promo-applied-row" accent="emerald"
                label={<span className="flex items-center gap-1.5"><Tag size={12} /> {applied.code} · {applied.label} <button data-testid="promo-clear-btn" onClick={clearPromo} className="text-[10px] text-slate-400 hover:text-red-500 underline ml-1">remove</button></span>}
                value={<span className="text-emerald-600 font-semibold">−₹{inr(applied.discount)}</span>} />
            )}
            <Line label={`GST @ 18%${applied ? " on discounted" : ""}`} value={`₹${inr(applied ? finalGst : invoice.gst)}`} muted />
            <div className="pt-3 border-t-2 border-dashed border-slate-200 flex items-center justify-between">
              <span className="font-display font-bold text-slate-900">Total due now</span>
              <span data-testid="invoice-total" className="font-mono font-bold text-2xl text-indigo-600">₹{inr(displayTotal)}</span>
            </div>
          </div>

          {!applied ? (
            <div className="mt-4 flex items-center gap-2">
              <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())}
                placeholder="Have a promo code? Try NEW25"
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
              <button data-testid="promo-apply-btn" onClick={applyPromo}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">Apply</button>
            </div>
          ) : (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-semibold flex items-center gap-2">
              <Sparkles size={13} /> You're saving ₹{inr(applied.discount)} — enjoy your launch discount!
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 text-sm">Pay via UPI <span className="text-[10px] font-semibold text-amber-500 border border-amber-200 bg-amber-50 rounded-full px-2 py-0.5 ml-2 align-middle">DEMO · preview</span></h3>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end mt-3">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">UPI VPA</label>
              <input data-testid="upi-vpa-input" value={vpa} onChange={(e) => setVpa(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 pb-2.5">
              <input type="checkbox" data-testid="autodebit-toggle" checked={autodebit} onChange={() => setAutodebit(!autodebit)} className="rounded" />
              <Repeat size={12} /> Auto-debit renewals
            </label>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 mt-4 text-xs text-slate-700 flex items-start gap-2">
            <ShieldCheck size={14} className="text-indigo-600 shrink-0 mt-0.5" />
            <span>Authorizing <b>{invoice.unit_count} units</b> at <b>₹{invoice.price_per_unit}/unit/month</b> for a total of <b>₹{inr(displayTotal)}</b>. Payment is collected through Atiti's existing rails.</span>
          </div>

          <button data-testid="upi-pay-confirm-button" onClick={pay} disabled={busy}
            className="btn-brand w-full rounded-full py-3.5 text-sm font-semibold mt-4 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} Confirm & pay ₹{inr(displayTotal)}
          </button>
        </div>
      </div>
    );
  }

  // ─── HOMESTAY-TIER (unchanged) ────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 reveal">
      <div className="bg-white rounded-2xl border border-slate-200 p-7">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-slate-900">Invoice — {property.name}</h2>
          <span data-testid="tier-badge" className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center gap-1">
            <Home size={11} /> Homestay · ₹475/unit
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-1">Same ₹475/unit rate as the OTA. One payment activates this whole property immediately.</p>
        <table className="w-full mt-5 text-sm">
          <tbody>
            {units.map((u) => (
              <tr key={u.id} className="border-b border-slate-100">
                <td className="py-2.5 text-slate-600">Unit {u.name} — PMS subscription (monthly)</td>
                <td className="py-2.5 text-right font-mono text-slate-700">₹{invoice.price_per_unit}</td>
              </tr>
            ))}
            <tr className="border-b border-slate-100">
              <td className="py-2.5 text-slate-500">Subtotal ({invoice.unit_count} units × ₹{invoice.price_per_unit})</td>
              <td className="py-2.5 text-right font-mono text-slate-700">₹{inr(invoice.subtotal)}</td>
            </tr>
            {applied && (
              <tr data-testid="promo-applied-row" className="border-b border-slate-100 bg-emerald-50/40">
                <td className="py-2.5 text-emerald-700 flex items-center gap-1.5 font-semibold">
                  <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-[10px]">{applied.code}</span>
                  {applied.label}
                  <button data-testid="promo-clear-btn" onClick={clearPromo} className="text-[10px] text-slate-400 hover:text-red-500 ml-1 underline">remove</button>
                </td>
                <td className="py-2.5 text-right font-mono text-emerald-600 font-semibold">−₹{inr(applied.discount)}</td>
              </tr>
            )}
            <tr className="border-b border-slate-100">
              <td className="py-2.5 text-slate-500">GST (18%){applied ? " on discounted subtotal" : ""}</td>
              <td className="py-2.5 text-right font-mono text-slate-700">₹{inr(applied ? finalGst : invoice.gst)}</td>
            </tr>
            <tr>
              <td className="py-3 font-display font-bold text-slate-900">Total due now</td>
              <td data-testid="invoice-total" className="py-3 text-right font-mono font-bold text-lg brand-pink">₹{inr(applied ? finalTotal : invoice.total)}</td>
            </tr>
          </tbody>
        </table>
        {!applied ? (
          <div className="mt-4 flex items-center gap-2">
            <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())}
              placeholder="Have a promo code? Try NEW25"
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
            <button data-testid="promo-apply-btn" onClick={applyPromo}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">Apply</button>
          </div>
        ) : (
          <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-semibold flex items-center gap-2">
            🎉 You're saving ₹{inr(applied.discount)} — enjoy your PMS launch discount!
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">Dual-trigger rule: a unit also activates automatically on its first booking — whichever happens first. Payment always precedes any connectivity-provider cost.</p>
      </div>

      <div className="space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-7">
          <h3 className="font-display font-bold text-slate-900">Pay via UPI <span className="text-[10px] font-semibold text-amber-500 border border-amber-200 bg-amber-50 rounded-full px-2 py-0.5 ml-2 align-middle">DEMO — simulated</span></h3>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mt-4">UPI VPA</label>
          <input data-testid="upi-vpa-input" value={vpa} onChange={(e) => setVpa(e.target.value)}
            className="mt-1.5 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
          <div className="flex items-center justify-between mt-5 bg-slate-50 rounded-xl px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Repeat size={14} /> Enable UPI auto-debit</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Optional — reuses your existing Atiti auto-debit mandate for renewals.</p>
            </div>
            <button data-testid="autodebit-toggle" onClick={() => setAutodebit(!autodebit)}
              className={`w-11 h-6 rounded-full transition-colors relative ${autodebit ? "bg-brand" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${autodebit ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl px-4 py-3 mt-5 text-xs text-slate-600">
            <ShieldCheck size={13} className="inline brand-pink mr-1" /> Cost confirmation: you are authorizing <b>{invoice.unit_count} billable units</b> for <b>₹{inr(applied ? finalTotal : invoice.total)}</b>. Payment is collected through Atiti's existing OTA payment rails.
          </div>
          <button data-testid="upi-pay-confirm-button" onClick={pay} disabled={busy}
            className="btn-brand w-full rounded-full py-3.5 text-sm font-semibold mt-5 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} Confirm & pay ₹{inr(applied ? finalTotal : invoice.total)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Line({ label, value, muted, accent, ...rest }) {
  return (
    <div {...rest} className={`flex items-center justify-between ${accent === "emerald" ? "text-emerald-700" : muted ? "text-slate-500" : "text-slate-700"}`}>
      <span className="text-sm">{label}</span>
      <span className="text-sm font-mono">{value}</span>
    </div>
  );
}
